/**
 * Payments Management API endpoints
 */
import { api } from './baseApi';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

export interface PaymentOrder {
  id: number;
  order_number: string;
  service_name: string;
  subservice_name: string;
  patient_name: string;
  scheduled_date: string;
  status: string;
}

export interface PaymentUser {
  id: number;
  full_name: string;
  email: string;
  phone_number: string;
}

export interface PaymentProvider {
  id: number;
  type: string;
  name: string;
  phone: string;
  email?: string;
}

export interface Payment {
  id: number;
  order: PaymentOrder;
  user: PaymentUser;
  provider: PaymentProvider | null;
  payment_method: string;
  amount: string;
  status: string;
  gateway_transaction_id: string;
  fawry_reference_number: string;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderDetails {
  patient_phone: string;
  patient_gender: string;
  patient_age: number;
  scheduled_time_from: string;
  scheduled_time_to: string;
  city: string;
  area: string;
  service_price: string;
  platform_fee: string;
  total_amount: string;
}

export interface PaymentDetail extends Payment {
  order_details: OrderDetails;
  service_amount: string;
  platform_fee: string;
  gateway_reference: string;
  fawry_merchant_ref_number: string;
  gateway_response: Record<string, unknown>;
  notes: string;
  failure_reason: string;
}

export interface PaymentStatistics {
  success: boolean;
  message: string;
  data: {
    overview: {
      total_payments: number;
      total_revenue: number;
      pending_amount: number;
      failed_amount: number;
      average_payment: number;
      status_breakdown: Array<{
        status: string;
        count: number;
        total_amount: string;
      }>;
      method_breakdown: Array<{
        payment_method: string;
        count: number;
        total_amount: string;
      }>;
    };
    payment_methods: Array<{
      payment_method: string;
      total_payments: number;
      successful_payments: number;
      failed_payments: number;
      pending_payments: number;
      total_revenue: number | null;
      average_amount: string;
    }>;
    top_paying_users: Array<unknown>;
  };
}

interface PaymentResponse {
  success: boolean;
  message: string;
  data: PaymentDetail;
}

interface PaymentsParams {
  payment_method?: string;
  payment_status?: string;
  order_id?: number;
  user_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export const paymentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentsStatistics: builder.query<PaymentStatistics, void>({
      query: () => '/dashboard/payments/statistics/',
      providesTags: ['Statistics'],
    }),
    getPayments: builder.query<PaginatedResponse<Payment>, PaymentsParams>({
      query: (params) => ({
        url: '/dashboard/payments/',
        params: {
          ...params,
          payment_status: params.payment_status,
          payment_method: params.payment_method,
        },
      }),
      providesTags: ['Payments'],
    }),
    getPayment: builder.query<PaymentResponse, number>({
      query: (id) => `/dashboard/payments/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Payment', id }],
    }),
    exportPayments: builder.query<Blob, PaymentsParams>({
      query: (params) => ({
        url: '/dashboard/payments/export/',
        params,
        responseHandler: (response) => response.blob(),
      }),
      providesTags: ['Payments'],
    }),
    updatePaymentStatus: builder.mutation<PaymentResponse, { id: number; data: UpdatePaymentStatusData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/payments/${id}/status/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Payment', id }, 'Payments', 'Statistics'],
    }),
  }),
});

interface UpdatePaymentStatusData {
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED';
  admin_notes?: string;
  gateway_transaction_id?: string;
  gateway_reference?: string;
  failure_reason?: string;
}

export const {
  useGetPaymentsStatisticsQuery,
  useGetPaymentsQuery,
  useGetPaymentQuery,
  useLazyExportPaymentsQuery,
  useUpdatePaymentStatusMutation,
} = paymentsApi;

export type { UpdatePaymentStatusData };

